import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CircleHelp, BookOpen, MapPinned, BookA, FileText } from "lucide-react";
import { loadIndex, topicForPath } from "../../lib/knowledgeApi";
import { cn } from "../../lib/utils";

function Item({ icon: Icon, title, sub, onClick, disabled, testid }) {
  return (
    <button data-testid={testid} disabled={disabled} onClick={onClick}
            className="w-full text-left px-4 py-2.5 flex items-start gap-3 hover:bg-surface-2 disabled:opacity-50 disabled:hover:bg-transparent transition-colors">
      <Icon size={15} className="text-primary mt-0.5 shrink-0" />
      <span className="min-w-0"><span className="block text-[13px] font-medium">{title}</span>{sub && <span className="block text-[11px] text-muted truncate">{sub}</span>}</span>
    </button>
  );
}

// Top-bar help: opens the hidden Knowledge Center or the topic for the current screen.
export default function HelpMenu() {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(null);
  const nav = useNavigate();
  const { pathname } = useLocation();
  const ref = useRef(null);

  useEffect(() => { loadIndex().then(setIndex).catch(() => {}); }, []);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", esc);
    return () => { window.removeEventListener("mousedown", close); window.removeEventListener("keydown", esc); };
  }, [open]);

  const slug = topicForPath(index, pathname);
  const title = slug && index?.areas.flatMap(a => a.groups.flatMap(g => g.topics)).find(t => t.slug === slug)?.title;
  const go = (to) => { setOpen(false); nav(to); };
  return (
    <div className="relative" ref={ref}>
      <button data-testid="help-button" onClick={() => setOpen(o => !o)} aria-label="Help and Knowledge Center" aria-expanded={open} title="Help · Knowledge Center"
              className={cn("h-9 w-9 inline-flex items-center justify-center border border-border bg-surface-2 hover:bg-primary/10 hover:border-primary/40 hover:text-primary rounded-sm transition-colors", open && "text-primary border-primary/50")}>
        <CircleHelp size={15} />
      </button>
      {open && (
        <div data-testid="help-menu" className="absolute right-0 top-11 w-72 bg-surface border border-border rounded-md shadow-xl z-50 overflow-hidden animate-fade-in py-1">
          <div className="px-4 pt-2 pb-1.5 text-[10px] uppercase tracking-[0.18em] text-muted font-semibold">Help</div>
          <Item icon={BookOpen} title="Knowledge Center" sub="Guides, worked examples and quizzes" onClick={() => go("/learn")} testid="help-menu-kc" />
          <Item icon={MapPinned} title="Learn about this page" sub={title || "No topic for this screen yet"} disabled={!slug}
                onClick={() => go(`/learn/${slug}`)} testid="help-menu-page" />
          <Item icon={BookA} title="Glossary" sub="Platform terms, A–Z" onClick={() => go("/learn/glossary")} testid="help-menu-glossary" />
          <div className="border-t border-border my-1" />
          <Item icon={FileText} title="Platform brief" sub="Opens in a new tab · Shift + ?" testid="help-menu-brief"
                onClick={() => { setOpen(false); window.open("/explainer.html", "_blank", "noopener,noreferrer"); }} />
        </div>
      )}
    </div>
  );
}
